import React, { useEffect } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Grid, Chip,
  Stack, Button, Divider, List, ListItem, ListItemIcon, ListItemText,
  Accordion, AccordionSummary, AccordionDetails, Table, TableBody,
  TableCell, TableRow, TableHead
} from '@mui/material';
import {
  Check, Business, ExpandMore, Groups, TrendingUp,
  NotificationsActive, Verified, ArrowForward
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

const LogicielFacturePME = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Logiciel facturation PME | Devis et factures pour petites entreprises | Facteio';

    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setProp = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Logiciel facturation PME : devis, factures, suivi paiements, relances automatiques, multi-utilisateurs. Conforme facture électronique 2026. Essai gratuit.');
    setMeta('keywords', 'logiciel facturation PME, logiciel devis facture PME, facture électronique PME, logiciel facturation petite entreprise, facturation 2026 PME');
    setProp('og:title', 'Logiciel facturation PME | Facteio');
    setProp('og:description', 'Facteio : logiciel facturation PME. Devis, factures, suivi clients, relances, conformité 2026.');

    let sd = document.getElementById('sd-pme');
    if (!sd) {
      sd = document.createElement('script');
      sd.id = 'sd-pme';
      sd.type = 'application/ld+json';
      document.head.appendChild(sd);
    }
    sd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Facteio",
      "applicationCategory": "BusinessApplication",
      "description": "Logiciel facturation PME. Devis, factures, multi-utilisateurs, facture électronique 2026.",
      "url": "https://facteio.com/logiciel-facture-pme",
      "offers": { "@type": "Offer", "priceCurrency": "EUR", "price": "0", "description": "Essai gratuit" }
    });

    return () => { const el = document.getElementById('sd-pme'); if (el) el.remove(); };
  }, []);

  const goToRegisterTracked = () => {
    const params = new URLSearchParams({
      utm_source: 'site_facteio', utm_medium: 'cta',
      utm_campaign: 'logiciel_facture_pme', utm_content: 'cta_page'
    });
    navigate(`/register?${params.toString()}`);
  };

  return (
    <Box sx={(theme) => ({ background: theme.palette.background.default, minHeight: '100vh' })}>

      {/* Hero */}
      <Box sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)', color: '#fff', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Button component={Link} to="/" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3, fontWeight: 600, '&:hover': { color: '#fff' } }}>
            ← Retour à l'accueil
          </Button>

          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={3}>
            <Chip icon={<Business sx={{ color: '#fff !important' }} />} label="PME & petite entreprise" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700 }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Multi-utilisateurs" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Relances automatiques" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
            <Chip icon={<Check sx={{ color: '#fff !important' }} />} label="Conforme 2026" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
          </Stack>

          <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 900, lineHeight: 1.15, mb: 2 }}>
            Logiciel facturation PME — simple, structuré, prêt pour 2026
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.1rem', md: '1.4rem' }, fontWeight: 400, opacity: 0.85, mb: 4, maxWidth: 700 }}>
            TPE, PME, petite entreprise : gérez vos devis, factures et clients depuis une seule interface.
            Suivi des paiements, relances automatiques, multi-sociétés — et conforme à la réforme de la facturation électronique 2026.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={goToRegisterTracked}
              sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 5, py: 1.8, fontSize: '1.05rem', fontWeight: 700 }}
            >
              Essayer gratuitement
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/pricing')}
              sx={{ borderColor: 'rgba(255,255,255,0.5)', color: '#fff', borderRadius: 50, px: 4, py: 1.8, fontWeight: 600 }}
            >
              Voir les tarifs
            </Button>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>

        {/* Problèmes PME */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={1} textAlign="center">
            Les vrais problèmes de facturation d'une PME
          </Typography>
          <Typography color="text.secondary" textAlign="center" mb={5} sx={{ maxWidth: 640, mx: 'auto' }}>
            Quand votre entreprise grandit, la complexité administrative grandit avec elle. Facteio est conçu pour absorber cette complexité sans alourdir vos process.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: <Groups sx={{ color: '#0f3460', fontSize: 32 }} />, title: 'Gestion multi-clients et multi-sociétés', text: 'Centralisez tous vos clients, avec leur historique de devis et factures. Si vous gérez plusieurs entités, chacune a son propre espace, sa propre numérotation et son propre logo.' },
              { icon: <TrendingUp sx={{ color: '#0f3460', fontSize: 32 }} />, title: 'Suivi en temps réel', text: 'Tableau de bord avec chiffre d\'affaires mensuel, factures en attente, factures payées, et retards de paiement — tout visible en un coup d\'œil.' },
              { icon: <NotificationsActive sx={{ color: '#0f3460', fontSize: 32 }} />, title: 'Relances automatiques', text: 'Paramétrez des relances automatiques à J+7, J+15 et J+30 après échéance. Vos impayés sont suivis sans effort, avec des emails personnalisables.' },
              { icon: <Verified sx={{ color: '#0f3460', fontSize: 32 }} />, title: 'Conformité légale et fiscale', text: 'Toutes les mentions légales françaises (TVA, RCS, SIRET, etc.) sont gérées automatiquement selon votre statut. Zéro risque en cas de contrôle fiscal.' },
            ].map((item, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card sx={{ height: '100%', border: '1px solid #E5E7EB', borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" mb={1.5}>
                      {item.icon}
                      <Box>
                        <Typography variant="h6" fontWeight={700} mb={0.5}>{item.title}</Typography>
                        <Typography color="text.secondary">{item.text}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Comparaison Facteio vs alternatives */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={4} textAlign="center">
            Pourquoi choisir Facteio pour votre PME ?
          </Typography>
          <Card sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#1a1a2e' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Fonctionnalité</TableCell>
                  <TableCell sx={{ color: '#FFD700', fontWeight: 700, fontSize: '1rem' }} align="center">Facteio</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700 }} align="center">Excel / Word</TableCell>
                  <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 700 }} align="center">ERP complexe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  ['Devis & factures pro en 2 min', true, false, true],
                  ['Numérotation automatique', true, false, true],
                  ['Relances automatiques', true, false, true],
                  ['Multi-sociétés', true, false, true],
                  ['Tableau de bord temps réel', true, false, true],
                  ['Facture électronique 2026', true, false, '→ 2026'],
                  ['Prix accessible PME', true, true, false],
                  ['Prise en main en 5 min', true, true, false],
                ].map(([feature, facteio, excel, erp], i) => (
                  <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                    <TableCell fontWeight="600">{feature}</TableCell>
                    <TableCell align="center">
                      {facteio === true ? <Check sx={{ color: 'green' }} /> : facteio}
                    </TableCell>
                    <TableCell align="center">
                      {excel === true ? <Check sx={{ color: 'green' }} /> : excel === false ? '✕' : excel}
                    </TableCell>
                    <TableCell align="center">
                      {erp === true ? <Check sx={{ color: 'green' }} /> : erp === false ? '✕' : erp}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Fonctionnalités détaillées */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={4}>
            Tout ce dont une PME a besoin
          </Typography>
          <Grid container spacing={3}>
            {[
              { title: 'Devis en ligne', items: ['Modèles personnalisables avec votre logo', 'Signature électronique du client', 'Conversion en facture en 1 clic', 'Validité du devis paramétrable'] },
              { title: 'Facturation complète', items: ['Factures pro avec toutes les mentions légales', 'Factures d\'acompte et de solde', 'Factures récurrentes automatiques', 'Numérotation chronologique conforme'] },
              { title: 'Suivi des paiements', items: ['Statut en temps réel (en attente, payée, en retard)', 'Relances automatiques par email', 'Notes de paiement internes', 'Export comptable CSV/Excel'] },
              { title: 'Espace clients', items: ['Fiche client complète avec historique', 'Plusieurs contacts par société', 'Notes et commentaires internes', 'Recherche rapide par nom ou SIRET'] },
            ].map((section, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card sx={{ height: '100%', border: '1px solid #E5E7EB', borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={700} mb={2} color="#0f3460">{section.title}</Typography>
                    <List dense disablePadding>
                      {section.items.map((item, j) => (
                        <ListItem key={j} disablePadding sx={{ mb: 0.75 }}>
                          <ListItemIcon sx={{ minWidth: 28 }}><Check sx={{ fontSize: 16, color: '#977F6B' }} /></ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Réforme 2026 PME */}
        <Box mb={8}>
          <Card sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', color: '#fff', borderRadius: 3, p: { xs: 3, md: 5 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Chip label="Obligation 2026" sx={{ bgcolor: '#977F6B', color: '#fff', fontWeight: 700, mb: 2 }} />
                <Typography variant="h4" fontWeight={800} mb={2}>
                  PME : la facture électronique est une obligation, pas un choix
                </Typography>
                <Typography sx={{ opacity: 0.85, mb: 3 }}>
                  À partir du <strong>1er septembre 2026</strong>, toutes les PME françaises devront émettre
                  leurs factures BtoB au format électronique via le Portail Public de Facturation (PPF) ou
                  une Plateforme de Dématérialisation Partenaire (PDP). Facteio est déjà prêt.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                  {['Format e-facture certifié', 'Transmission PPF native', 'Archivage légal 10 ans', 'Aucune migration requise'].map((t, i) => (
                    <Chip key={i} icon={<Check sx={{ color: '#fff !important' }} />} label={t} sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' }} />
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/reforme-2026')}
                  sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 4, py: 1.5, fontWeight: 700 }}
                >
                  En savoir plus
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Box>

        {/* FAQ */}
        <Box mb={8}>
          <Typography variant="h3" fontWeight={800} mb={4}>
            Questions fréquentes — PME
          </Typography>
          {[
            { q: 'Quel logiciel de facturation pour une PME en France ?', a: 'Facteio est conçu pour les TPE et PME françaises : devis, factures, suivi clients, relances automatiques et conformité à la réforme 2026. Simple à déployer (5 minutes), sans formation technique requise.' },
            { q: 'Comment gérer les relances clients avec un logiciel de facturation ?', a: 'Facteio envoie automatiquement des emails de relance aux clients en retard de paiement. Vous paramétrez les délais (ex : J+7, J+15, J+30 après échéance) et le ton du message. Plus besoin de suivre manuellement chacun.' },
            { q: 'Peut-on gérer plusieurs sociétés avec Facteio ?', a: 'Oui. Facteio supporte la gestion multi-sociétés : chaque entité a son propre espace, sa propre numérotation de documents et ses propres informations légales et logo.' },
            { q: 'La facture électronique est-elle obligatoire pour les PME en 2026 ?', a: 'Oui. À partir du 1er septembre 2026, toutes les PME françaises (et les TPE, et les auto-entrepreneurs) devront émettre et recevoir des factures électroniques pour leurs transactions avec d\'autres professionnels. Facteio vous prépare dès maintenant.' },
            { q: 'Combien coûte un logiciel de facturation pour PME ?', a: 'Facteio propose un démarrage gratuit, puis des abonnements adaptés à la taille de votre structure. Consultez notre page tarifs pour voir les offres. Pas de frais cachés, résiliable à tout moment.' },
          ].map((faq, i) => (
            <Accordion key={i} sx={{ mb: 1, border: '1px solid #E5E7EB', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ '& .MuiAccordionSummary-content': { fontWeight: 700 } }}>
                {faq.q}
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Liens internes */}
        <Box mb={8}>
          <Typography variant="h5" fontWeight={700} mb={3}>Vous cherchez aussi :</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" gap={2}>
            <Button variant="outlined" component={Link} to="/logiciel-devis-facture-artisan" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Logiciel devis facture artisan →
            </Button>
            <Button variant="outlined" component={Link} to="/logiciel-facture-auto-entrepreneur" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Logiciel facture auto-entrepreneur →
            </Button>
            <Button variant="outlined" component={Link} to="/reforme-2026" sx={{ borderRadius: 50, fontWeight: 600 }}>
              Réforme facture électronique 2026 →
            </Button>
          </Stack>
        </Box>

        {/* CTA final */}
        <Card sx={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', color: '#fff', borderRadius: 3, p: { xs: 3, md: 5 }, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={800} mb={1}>
            Structurez votre facturation, maintenant
          </Typography>
          <Typography sx={{ opacity: 0.9, mb: 3, maxWidth: 520, mx: 'auto' }}>
            Devis, factures, relances et conformité 2026 — tout en un.
            Essayez Facteio gratuitement, sans carte bancaire.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={goToRegisterTracked}
            sx={{ bgcolor: '#977F6B', '&:hover': { bgcolor: '#7A6358' }, borderRadius: 50, px: 5, py: 1.8, fontWeight: 700 }}
          >
            Créer mon compte gratuit
          </Button>
        </Card>

      </Container>
    </Box>
  );
};

export default LogicielFacturePME;
